import { COLORS, BG_BASE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";
import MetricCopyright from "../common/MetricCopyright";

const sampleData = {
  teamAvg: 0.64,
  members: [
    { name: "Michael Monske", role: "SRE",             dir: 1.488,  projects: 37, confidence: 1.0 },
    { name: "Todd Korando",   role: "DevOps Engineer", dir: 0.532,  projects: 30, confidence: 1.0 },
    { name: "Shirley Cortez", role: "SQL Developer",   dir: -0.090, projects: 28, confidence: 1.0 },
  ],
};

function DIRBar({ value, maxAbsValue }) {
  const isPositive = value >= 0;
  const barPct = Math.min((Math.abs(value) / maxAbsValue) * 50, 50);
  const color = isPositive ? COLORS.green : COLORS.red;
  return (
    <div style={{ position: "relative", width: "100%", height: 20 }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 20, background: BG_BASE, borderRadius: 4 }} />
      <div style={{ position: "absolute", top: 0, left: "50%", width: 2, height: 20, background: COLORS.cardBorder, zIndex: 2 }} />
      <div style={{
        position: "absolute", top: 3, height: 14, borderRadius: 3,
        background: `linear-gradient(${isPositive ? "90deg" : "270deg"}, ${color}60, ${color})`,
        zIndex: 1, transition: "width 0.6s ease, left 0.6s ease",
        ...(isPositive ? { left: "50%", width: `${barPct}%` } : { left: `${50 - barPct}%`, width: `${barPct}%` }),
      }} />
    </div>
  );
}

function getDIRLabel(dir) {
  if (dir > 0.5)   return "Strong positive";
  if (dir > 0.1)   return "Above average";
  if (dir >= -0.1) return "Near average";
  if (dir >= -0.5) return "Below average";
  return "Underperformance";
}

export default function DIRPlusMinusChart({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const members = d.members || [];
  const teamAvg = d.teamAvg;
  const maxAbsValue = Math.max(...members.map(m => Math.abs(m.dir || 0)), 0.1);
  const sorted = [...members].sort((a, b) => (b.dir || 0) - (a.dir || 0));

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Team <MetricCopyright name="DIR" /> Analysis</h3>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            Delivery Impact Rating© — measures track record of successful project delivery (like +/− in sports)
          </div>
        </div>
        {teamAvg != null && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase" }}>Team Avg</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: teamAvg >= 0 ? COLORS.green : COLORS.red }}>
              {teamAvg >= 0 ? "+" : ""}{teamAvg.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, padding: "0 4px" }}>
        <span style={{ fontSize: 9, color: COLORS.red,  textTransform: "uppercase" }}>← Below Average</span>
        <span style={{ fontSize: 9, color: COLORS.textMuted }}>Baseline</span>
        <span style={{ fontSize: 9, color: COLORS.green, textTransform: "uppercase" }}>Above Average →</span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {sorted.map((m, i) => {
          const isPositive = (m.dir || 0) >= 0;
          const color = isPositive ? COLORS.green : COLORS.red;
          return (
            <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <ClickableName name={m.name} onClick={onEmployeeClick} style={{ fontSize: 13, fontWeight: 600 }} />
                  <span style={{ fontSize: 10, color: COLORS.textMuted }}>({m.role})</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color }}>{isPositive ? "+" : ""}{(m.dir || 0).toFixed(2)}</span>
                  {m.projects != null && <span style={{ fontSize: 10, color: COLORS.textMuted }}>{m.projects} projects</span>}
                </div>
              </div>
              <DIRBar value={m.dir || 0} maxAbsValue={maxAbsValue} />
              <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2, textAlign: isPositive ? "right" : "left" }}>
                {getDIRLabel(m.dir || 0)}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${COLORS.cardBorder}`, fontSize: 10, color: COLORS.textMuted, lineHeight: 1.6 }}>
        DIR is a WAR-style metric using replacement-level baseline. Positive values mean the employee's engagements performed above the 25th percentile baseline.
      </div>
    </div>
  );
}
